// --- PromptModal.jsx ---
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';

const PromptModal = ({ isOpen, onClose, prompt }) => {
  if (!isOpen || !prompt) return null;

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      console.log(`${type} copied to clipboard!`);
    } catch {
      console.error('Failed to copy to clipboard');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">
            Prompt Details: {prompt.name}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-xl"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Name
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{prompt.name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Prompt ID
                </label>
                <div className="flex items-center space-x-2 mt-1">
                  <p className="text-sm text-gray-900 dark:text-white font-mono">{prompt.prompt_id}</p>
                  <button
                    onClick={() => copyToClipboard(prompt.prompt_id, 'Prompt ID')}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    title="Copy Prompt ID"
                  >
                    <FontAwesomeIcon icon={faCopy} size="sm" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Agent ID
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{prompt.agent_id || 'Not set'}</p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Description
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{prompt.description || 'No description available'}</p>
              </div>
            </div>
          </div>

          {/* Prompt Template */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Prompt Template</h3>
              <button
                onClick={() => copyToClipboard(prompt.prompt_template, 'Prompt Template')}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1 rounded"
                title="Copy Prompt Template"
              >
                <FontAwesomeIcon icon={faCopy} className="mr-1" />
                Copy
              </button>
            </div>
            <pre className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-8 00 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap border">
              {prompt.prompt_template || 'No template available'}
            </pre>
          </div>

          {/* Model Configuration */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Model Configuration</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Temperature
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{prompt.temperature !== undefined ? prompt.temperature : 'Not set'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Top P
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{prompt.top_p !== undefined ? prompt.top_p : 'Not set'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Max Output Tokens
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{prompt.max_output_tokens || 'Not set'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Output Format
                </label>
                <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full ${
                  prompt.output_format === 'json' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  prompt.output_format === 'text' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                }`}>
                  {prompt.output_format || 'Not set'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tools Enabled
                </label>
                <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full ${
                  prompt.tool_enabled 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                }`}>
                  {prompt.tool_enabled !== undefined ? (prompt.tool_enabled ? 'Enabled' : 'Disabled') : 'Not set'}
                </span>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Tool Choice
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{prompt.tool_choice || 'Not set'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Stream
                </label>
                <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full ${
                  prompt.stream 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                }`}>
                  {prompt.stream !== undefined ? (prompt.stream ? 'Enabled' : 'Disabled') : 'Not set'}
                </span>
              </div>
            </div>
          </div>

          {/* Text Format Settings */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Text Format Settings</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Text Format Name
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{prompt.text_format_name || 'Not set'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Text Format Type
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{prompt.text_format_type || 'Not set'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Text Format Strict
                </label>
                <span className={`inline-block mt-1 px-2 py-1 text-xs font-medium rounded-full ${
                  prompt.text_format_strict 
                    ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200'
                    : 'bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200'
                }`}>
                  {prompt.text_format_strict !== undefined ? (prompt.text_format_strict ? 'Strict' : 'Flexible') : 'Not set'}
                </span>
              </div>
            </div>
          </div>

          {/* Output Schema */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Output Schema</h3>
              {prompt.output_schema && (
                <button
                  onClick={() => copyToClipboard(JSON.stringify(prompt.output_schema, null, 2), 'Output Schema')}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1 rounded"
                  title="Copy Output Schema"
                >
                  <FontAwesomeIcon icon={faCopy} className="mr-1" />
                  Copy
                </button>
              )}
            </div>
            <pre className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap border">
              {prompt.output_schema ? JSON.stringify(prompt.output_schema, null, 2) : 'No output schema configured'}
            </pre>
          </div>

          {/* Text Format Schema */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Text Format Schema</h3>
              {prompt.text_format_schema && (
                <button
                  onClick={() => copyToClipboard(JSON.stringify(prompt.text_format_schema, null, 2), 'Text Format Schema')}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1 rounded"
                  title="Copy Text Format Schema"
                >
                  <FontAwesomeIcon icon={faCopy} className="mr-1" />
                  Copy
                </button>
              )}
            </div>
            <pre className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap border">
              {prompt.text_format_schema ? JSON.stringify(prompt.text_format_schema, null, 2) : 'No text format schema configured'}
            </pre>
          </div>

          {/* Assigned Tools */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Assigned Tools</h3>
              {prompt.assigned_tools && (
                <button
                  onClick={() => copyToClipboard(JSON.stringify(prompt.assigned_tools, null, 2), 'Assigned Tools')}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1 rounded"
                  title="Copy Assigned Tools"
                >
                  <FontAwesomeIcon icon={faCopy} className="mr-1" />
                  Copy
                </button>
              )}
            </div>
            <pre className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap border">
              {prompt.assigned_tools ? JSON.stringify(prompt.assigned_tools, null, 2) : 'No tools assigned'}
            </pre>
          </div>

          {/* Structured Output */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Structured Output Configuration</h3>
              {prompt.structured_output && (
                <button
                  onClick={() => copyToClipboard(JSON.stringify(prompt.structured_output, null, 2), 'Structured Output')}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-2 py-1 rounded"
                  title="Copy Structured Output Configuration"
                >
                  <FontAwesomeIcon icon={faCopy} className="mr-1" />
                  Copy
                </button>
              )}
            </div>
            <pre className="text-sm text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-800 p-4 rounded-lg overflow-x-auto whitespace-pre-wrap border">
              {prompt.structured_output ? JSON.stringify(prompt.structured_output, null, 2) : 'No structured output configuration'}
            </pre>
          </div>

          {/* Variables (legacy) */}
          {prompt.variables && prompt.variables.length > 0 && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Variables</h3>
              <div className="space-y-2">
                {prompt.variables.map((variable, index) => (
                  <div key={index} className="bg-blue-50 dark:bg-blue-900 p-2 rounded">
                    <code className="text-sm">{variable}</code>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Metadata</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Created At
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {prompt.created_at ? new Date(prompt.created_at).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  }) : 'Not set'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Last Updated
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">
                  {prompt.updated_at ? new Date(prompt.updated_at).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                  }) : 'Not set'}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Created By
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white">{prompt.created_by || 'Not set'}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Version
                </label>
                <p className="mt-1 text-sm text-gray-900 dark:text-white font-mono">{prompt.version || 'Not set'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromptModal;